import { Button } from "@/components/ui/baseUi/button"
import Dropzone, { type DropzoneRef } from "@/components/ui/baseUi/dropzone"
import { Input } from "@/components/ui/baseUi/input"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import type { MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { MediaType } from "@/modules/common/media/mediaTypes"
import { cn } from "@/modules/lib/utils"
import { MediaExistingBlockImage } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocImage"
import { MediaExistingBlockPdf } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocPdf"
import { MediaExistingBlockVideo } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocVideo"
import { MediaExistingBlockVimeo } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocVimeo"
import { MediaExistingBlockYoutube } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaExistingBlocYoutube"
import type { MediaBlockItemFormikType } from "@/modules/sessions/sessionForm/blocks/mediaBlock/mediaBlockFormikTypes"
import { VimeoIcon } from "@images/svgIcons/medias/vimeoIcon"
import { YoutubeFileIcon } from "@images/svgIcons/medias/youtubeFileIcon"
import { PlusIcon } from "@images/svgIcons/plusIcon"
import { word } from "@modules/core/utils/i18n"
import { VIDEO_MAX_SIZE } from "@modules/ui/components/fileUpload/fileUploadUtils"
import { useUploadMedia } from "@modules/ui/components/fileUpload/hooks/useUploadMedia"
import { MediaUploadDragZone } from "@modules/ui/components/fileUpload/mediaUploadDragZone"
import {
  checkVimeoVideoExists,
  checkYoutubeVideoExists,
  extractVimeoId,
  isVimeoLink,
  isYoutubeLink,
} from "@modules/utils/mediaUtils"
import { CheckIcon, LoaderCircle, X } from "lucide-react"
import { useRef, useState } from "react"

type MediaUploadProps = (
  | {
      type: "mediaItem"
      mediaList: MediaItemFormType[]
      updateMediaList: (mediaList: MediaItemFormType[]) => void
      updateMediaItem: (mediaItem: MediaItemFormType, index: number) => void
    }
  | {
      type: "mediaBlockItem"
      mediaList: MediaBlockItemFormikType[]
      updateMediaList: (mediaList: MediaBlockItemFormikType[]) => void
      updateMediaItem: (mediaItem: MediaBlockItemFormikType, index: number) => void
    }
) &
  React.ComponentProps<"div">

export function MediaUpload({
  type,
  mediaList,
  updateMediaList,
  updateMediaItem,
  className,
  ...props
}: MediaUploadProps) {
  const [displayVideoUrlInput, setDisplayVideoUrlInput] = useState<"youtube" | "vimeo" | false>(false)
  const [videoUrl, setVideoUrl] = useState("")

  const { uploadMedia } = useUploadMedia()

  const fileUploadRef = useRef<DropzoneRef>(null)

  const openFileSelector = () => {
    fileUploadRef.current?.openFileDialog()
  }

  const onSelectFile = async (files: File[]) => {
    try {
      await Promise.all(
        files.map(async (mediaFile: File) => {
          if (mediaFile.size > VIDEO_MAX_SIZE) {
            toast.error(word("global.error.label"), {
              description: word("video_upload.max_size.error", { maxSize: VIDEO_MAX_SIZE / 1_000_000 }),
              classNames: errorToastClassName(),
            })
            return
          }

          await onUploadNewMedia(mediaFile)
        }),
      )
    } catch (e: unknown) {
      if (e instanceof Error) {
        console.error(`[MediaUpload] ${e.message}`)
      } else {
        console.error(`[MediaUpload] ${String(e)}`)
      }
      toast.error(word("global.error.label"), {
        description: word("global.fileUpload.error"),
        classNames: errorToastClassName(),
      })
    }
  }

  const onUploadNewMedia = async (file: File) => {
    if (!file.type.includes("video/") && !file.type.includes("image/") && file.type !== "application/pdf") return
    let newMedia: MediaItemFormType | MediaBlockItemFormikType
    let newItemIndex = -1

    try {
      switch (type) {
        case "mediaItem": {
          newMedia = {
            url: null,
            file,
            key: null,
            type: file.type.includes("video/")
              ? MediaType.S3_VIDEO
              : file.type === "application/pdf"
                ? MediaType.S3_PDF
                : MediaType.S3_IMAGE,
            isUploading: true,
            filename: file.name,
          } as MediaItemFormType

          updateMediaList([...mediaList, newMedia] as MediaItemFormType[])

          newItemIndex = mediaList.length

          const response = await uploadMedia(file)
          if (!response.awsS3Key) {
            throw new Error("Failed to upload media")
          }

          const newMediaItem = {
            ...newMedia,
            key: response.awsS3Key,
            isUploading: false,
          }

          updateMediaItem(newMediaItem, newItemIndex)
          break
        }
        case "mediaBlockItem": {
          newMedia = {
            id: null,
            url: null,
            file,
            awsS3Key: null,
            type: file.type.includes("video/")
              ? MediaType.S3_VIDEO
              : file.type === "application/pdf"
                ? MediaType.S3_PDF
                : MediaType.S3_IMAGE,
            isUploading: true,
            filename: file.name,
          } as MediaBlockItemFormikType

          updateMediaList([...mediaList, newMedia] as MediaBlockItemFormikType[])

          newItemIndex = mediaList.length

          const response = await uploadMedia(file)
          if (!response.awsS3Key) {
            throw new Error("Failed to upload media")
          }

          const newMediaItem = {
            ...newMedia,
            awsS3Key: response.awsS3Key,
            isUploading: false,
          }

          updateMediaItem(newMediaItem, newItemIndex)
          break
        }
        default: {
          const _exhaustiveCheck: never = type
          return _exhaustiveCheck
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error(`[MediaUpload] ${error.message}`)
      } else {
        console.error(`[MediaUpload] ${String(error)}`)
      }
      toast.error(word("global.error.label"), {
        description: word("global.fileUpload.error"),
        classNames: errorToastClassName(),
      })

      if (newItemIndex > -1) removeMediaItem(newItemIndex)
    }
  }

  const removeMediaItem = (index: number) => {
    switch (type) {
      case "mediaItem": {
        const newMediaList = [...mediaList]
        newMediaList.splice(index, 1)
        updateMediaList(newMediaList)
        break
      }
      case "mediaBlockItem": {
        const newMediaList = [...mediaList]
        newMediaList.splice(index, 1)
        updateMediaList(newMediaList)
        break
      }
      default: {
        const _exhaustiveCheck: never = type
        return _exhaustiveCheck
      }
    }
  }

  const onAddVideoLink = async () => {
    if (displayVideoUrlInput === "youtube") {
      if (
        !videoUrl ||
        videoUrl.length === 0 ||
        !isYoutubeLink(videoUrl) ||
        !(await checkYoutubeVideoExists(videoUrl))
      ) {
        toast.error(word("global.error.label"), {
          description: word("sessionForm.blocks.add_media.youtube_error"),
          classNames: errorToastClassName(),
        })
        return
      }
      switch (type) {
        case "mediaItem": {
          const newMedia: MediaItemFormType = {
            url: videoUrl,
            key: null,
            file: null,
            type: MediaType.YOUTUBE,
            filename: null,
            isUploading: false,
          }
          updateMediaList([...mediaList, newMedia] as MediaItemFormType[])
          break
        }
        case "mediaBlockItem": {
          const newMedia: MediaBlockItemFormikType = {
            id: null,
            url: videoUrl,
            awsS3Key: null,
            file: null,
            type: MediaType.YOUTUBE,
            isUploading: false,
            filename: null,
          }
          updateMediaList([...mediaList, newMedia] as MediaBlockItemFormikType[])
          break
        }
        default: {
          const _exhaustiveCheck: never = type
          return _exhaustiveCheck
        }
      }
      setVideoUrl("")
      setDisplayVideoUrlInput(false)
    } else if (displayVideoUrlInput === "vimeo") {
      const videoId = extractVimeoId(videoUrl)
      if (
        !videoUrl ||
        !videoId ||
        !isVimeoLink(videoUrl) ||
        !extractVimeoId(videoUrl) ||
        !(await checkVimeoVideoExists(videoId))
      ) {
        toast.error(word("global.error.label"), {
          description: word("sessionForm.blocks.add_media.vimeo_error"),
          classNames: errorToastClassName(),
        })
        return
      }

      switch (type) {
        case "mediaItem": {
          const newMedia: MediaItemFormType = {
            url: `https://vimeo.com/${videoId}`,
            key: null,
            file: null,
            type: MediaType.VIMEO,
            filename: null,
            isUploading: false,
          }
          updateMediaList([...mediaList, newMedia] as MediaItemFormType[])
          break
        }
        case "mediaBlockItem": {
          const newMedia: MediaBlockItemFormikType = {
            id: null,
            url: `https://vimeo.com/${videoId}`,
            awsS3Key: null,
            file: null,
            type: MediaType.VIMEO,
            isUploading: false,
            filename: null,
          }
          updateMediaList([...mediaList, newMedia] as MediaBlockItemFormikType[])
          break
        }
        default: {
          const _exhaustiveCheck: never = type
          return _exhaustiveCheck
        }
      }
      setVideoUrl("")
      setDisplayVideoUrlInput(false)
    }
  }

  return (
    <div
      className={cn("flex grow flex-col overflow-hidden rounded-3xl border border-borderDefault", className)}
      {...props}
    >
      {displayVideoUrlInput ? (
        <div className="flex flex-row items-center justify-start gap-3 bg-surface-foreground p-3">
          <Input
            placeholder={
              displayVideoUrlInput === "youtube"
                ? word("sessionForm.blocks.add_media.youtube_link.placeholder")
                : word("sessionForm.blocks.add_media.vimeo_link.placeholder")
            }
            value={videoUrl}
            onChange={(e) => {
              setVideoUrl(e.currentTarget.value)
            }}
            className="grow bg-surface-background"
          />

          <Button variant="primary" size="iconMd" onClick={() => void onAddVideoLink()}>
            <CheckIcon color="currentColor" className="text-textDefault-body" size={12} />
          </Button>

          <Button
            variant="ghost"
            size="iconMd"
            onClick={() => {
              setDisplayVideoUrlInput(false)
              setVideoUrl("")
            }}
          >
            <X color="currentColor" className="text-textDefault-body" size={12} />
          </Button>
        </div>
      ) : (
        <div className="flex flex-row items-center justify-start gap-3 bg-surface-foreground p-3">
          <Button
            size="sm"
            variant="outline"
            onClick={openFileSelector}
            className="border-0 ring-neutral-200 hover:ring-1"
          >
            <PlusIcon color="currentColor" height={24} className="text-textDefault-body" />
            <Text fontStyle="LS">{word("sessionForm.blocks.add_media.upload")}</Text>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDisplayVideoUrlInput("youtube")
            }}
            className="border-0 ring-neutral-200 hover:ring-1"
          >
            <YoutubeFileIcon style={{ height: 24 }} color="currentColor" className="text-textDefault-body" />
            <Text fontStyle="LS">{word("sessionForm.blocks.add_media.add_youtube_link")}</Text>
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setDisplayVideoUrlInput("vimeo")
            }}
            className="border-0 ring-neutral-200 hover:ring-1"
          >
            <VimeoIcon style={{ height: 24 }} color="currentColor" className="text-textDefault-body" />
            <Text fontStyle="LS">{word("sessionForm.blocks.add_media.add_vimeo_link")}</Text>
          </Button>
        </div>
      )}

      <div className="border-t border-r-0 border-b-0 border-l-0 border-dotted border-borderDefault">
        <Dropzone
          ref={fileUploadRef}
          onFilesAdded={(files) => void onSelectFile(files)}
          className={cn("w-full transition-opacity **:cursor-pointer hover:opacity-60")}
          accept={"image/jpeg,image/png,video/mp4,video/quicktime,application/pdf"}
          style={{ height: 240 }}
        >
          <MediaUploadDragZone />
        </Dropzone>
      </div>

      {mediaList.map((mediaItem, index) => (
        <div
          className="flex h-33.5 flex-row items-center justify-between gap-3 border-t border-r-0 border-b-0 border-l-0 border-dotted border-borderDefault bg-surface-foreground px-3"
          key={index}
        >
          {mediaItem.type === MediaType.S3_VIDEO ? (
            <MediaExistingBlockVideo key={index} media={mediaItem} />
          ) : mediaItem.type === MediaType.S3_IMAGE ? (
            <MediaExistingBlockImage
              key={index}
              media={mediaItem}
              style={{ maxHeight: 120, minHeight: "100%", height: 0, padding: "15px 0" }}
            />
          ) : mediaItem.type === MediaType.S3_PDF ? (
            <MediaExistingBlockPdf key={index} media={mediaItem} />
          ) : mediaItem.type === MediaType.YOUTUBE && mediaItem.url && isYoutubeLink(mediaItem.url) ? (
            <MediaExistingBlockYoutube key={index} media={mediaItem} />
          ) : mediaItem.type === MediaType.VIMEO && mediaItem.url && isVimeoLink(mediaItem.url) ? (
            <MediaExistingBlockVimeo key={index} media={mediaItem} id={index.toString()} />
          ) : (
            <Text fontStyle="BM" className="wrap-break-word" key={index}>
              {word("sessionForm.blocks.add_media.missing_error")}
            </Text>
          )}

          {mediaItem.isUploading ? (
            <LoaderCircle className="animate-spin" />
          ) : (
            <Button
              variant="ghost"
              size="iconMd"
              onClick={() => {
                removeMediaItem(index)
              }}
            >
              <X color="currentColor" className="text-textDefault-body" />
            </Button>
          )}
        </div>
      ))}
    </div>
  )
}
